const ALGORITHM = 'AES-GCM' as const
const KEY_LENGTH = 256 as const
const IV_BYTE_LENGTH = 12 as const

const importKey = async (rawBase64: string): Promise<CryptoKey> => {
  const keyBytes = base64ToBytes(rawBase64)
  return crypto.subtle.importKey('raw', keyBytes, { name: ALGORITHM }, false, [
    'encrypt',
    'decrypt',
  ])
}

export const encryptAesGcm = async (
  plaintext: Uint8Array,
  rawBase64Key: string,
): Promise<{ readonly ciphertext: string; readonly iv: string }> => {
  const iv = crypto.getRandomValues(new Uint8Array(IV_BYTE_LENGTH))
  const cryptoKey = await importKey(rawBase64Key)

  const encrypted = await crypto.subtle.encrypt(
    { name: ALGORITHM, iv, tagLength: 128 },
    cryptoKey,
    plaintext,
  )

  return {
    ciphertext: bytesToBase64(new Uint8Array(encrypted)),
    iv: bytesToBase64(iv),
  }
}

export const decryptAesGcm = async (
  ciphertextBase64: string,
  ivBase64: string,
  rawBase64Key: string,
): Promise<Uint8Array> => {
  const ciphertext = base64ToBytes(ciphertextBase64)
  const iv = base64ToBytes(ivBase64)
  const cryptoKey = await importKey(rawBase64Key)

  const decrypted = await crypto.subtle.decrypt(
    { name: ALGORITHM, iv, tagLength: 128 },
    cryptoKey,
    ciphertext,
  )

  return new Uint8Array(decrypted)
}

export const generateRawAesKey = async (): Promise<string> => {
  const cryptoKey = await crypto.subtle.generateKey({ name: ALGORITHM, length: KEY_LENGTH }, true, [
    'encrypt',
    'decrypt',
  ])

  const exported = await crypto.subtle.exportKey('raw', cryptoKey)
  return bytesToBase64(new Uint8Array(exported))
}

const bytesToBase64 = (bytes: Uint8Array): string => {
  const binString = Array.from(bytes, (byte) => String.fromCodePoint(byte)).join('')
  return btoa(binString)
}

const base64ToBytes = (base64: string): Uint8Array => {
  const binString = atob(base64)
  return Uint8Array.from(binString, (char) => char.codePointAt(0) ?? 0)
}
